import {
  Box,
  Button,
  SegmentedControl,
  Container,
  Flex,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { ColorInput } from "react-hook-form-mantine";
import ImageUpload from "../../../ImageUpload";
import MobilePreview from "../MobilePreview";
import { useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import resolver from "@congrs/resolver";
import {
  DEFAULT_EVENT_ACCENT_COLOR,
  web_patchCustomEventSchema,
} from "@congrs/schemas";
import { fullFormats } from "ajv-formats/dist/formats";
import { colorSwatches } from "../utils/colorUtils";
import { extractDirtyData } from "../../../../utils/dirtyData";
import { usePatchEvent } from "../../../../api/event";

const PREVIEW_HOME = "/preview";
const PREVIEW_COVERS = "/preview/covers";

/** The covers the frame can show, in the order the form lists them. */
const COVERS = [
  { label: "Profile", field: "defaultProfileCover" },
  { label: "Session", field: "defaultSessionCover" },
  { label: "Sponsor", field: "defaultSponsorCover" },
  { label: "Speaker", field: "defaultSpeakerCover" },
];

/** The frame's routes, as the preview panel's switcher offers them. */
const PREVIEW_VIEWS = [
  { label: "Home screen", path: PREVIEW_HOME },
  { label: "Cover images", path: PREVIEW_COVERS },
];

const CustomizationTab = ({ data, showPreview }) => {
  const { config = {} } = data;
  const {
    logoImage = "",
    headerImage = "",
    defaultProfileCover = "",
    defaultSessionCover = "",
    defaultSponsorCover = "",
    defaultSpeakerCover = "",
  } = config;

  const { mutate, isPending: isLoading } = usePatchEvent(data?.id);

  const defaultValues = useMemo(() => {
    return {
      config: {
        logoImage: data?.config?.logoImage || "",
        headerImage: data?.config?.headerImage || "",
        accentColor: data?.config?.accentColor || DEFAULT_EVENT_ACCENT_COLOR,
        defaultProfileCover: data?.config?.defaultProfileCover || "",
        defaultSessionCover: data?.config?.defaultSessionCover || "",
        defaultSponsorCover: data?.config?.defaultSponsorCover || "",
        defaultSpeakerCover: data?.config?.defaultSpeakerCover || "",
      },
    };
  }, [data]);

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields },
    reset,
  } = useForm({
    defaultValues,
    resolver: resolver(web_patchCustomEventSchema, { formats: fullFormats }),
  });

  // A picked image is only an upload key until the event is saved, so the
  // preview gets the inline copy `ImageUpload` hands over instead.
  const [pickedImages, setPickedImages] = useState({});
  // Which of the frame's routes the panel is showing; picking an image jumps
  // to the route that shows it.
  const [previewPath, setPreviewPath] = useState(PREVIEW_HOME);
  // Which cover the frame shows, so picking a speaker cover doesn't land on
  // the profile one.
  const [previewCover, setPreviewCover] = useState(COVERS[0].field);

  const onPick = (field, path) => (image) => {
    setPickedImages((current) => ({ ...current, [field]: image }));
    setPreviewPath(path);
    if (COVERS.some((cover) => cover.field === field)) setPreviewCover(field);
  };

  const draft = useWatch({ control, name: "config" });

  // The accent color is the home screen's to show; a change to it pulls the
  // frame back there from the cover images.
  const accentColor = draft?.accentColor;
  useEffect(() => setPreviewPath(PREVIEW_HOME), [accentColor]);

  const previewConfig = useMemo(() => {
    const image = (field) => pickedImages[field] ?? draft?.[field] ?? "";

    return {
      logoImage: image("logoImage"),
      headerImage: image("headerImage"),
      defaultProfileCover: image("defaultProfileCover"),
      defaultSessionCover: image("defaultSessionCover"),
      defaultSponsorCover: image("defaultSponsorCover"),
      defaultSpeakerCover: image("defaultSpeakerCover"),
      accentColor: draft?.accentColor,
    };
  }, [draft, pickedImages]);

  const onSubmit = (data) => {
    const dirtyData = extractDirtyData(data, dirtyFields);

    if (dirtyFields.config) {
      const configChanges = {};
      Object.keys(dirtyFields.config).forEach((key) => {
        configChanges[key] = data.config[key];
      });
      if (Object.keys(configChanges).length > 0) {
        dirtyData.config = configChanges;
      }
    }

    mutate(dirtyData, {
      onSuccess: () => {
        reset(data);
      },
    });
  };

  return (
    <Container py="md" size="xl">
      <Flex
        gap="lg"
        direction={{ base: "column", lg: "row" }}
        align={{ base: "stretch", lg: "flex-start" }}
      >
        <Box flex={1}>
          <Paper withBorder shadow="md" p="md" px="lg" radius="md">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="xl">
                <section>
                  <Title order={3}>Home page images</Title>
                  <Text c="dimmed" size="sm" mb="md">
                    Images that are shown on the home page of the mobile event
                    application.
                  </Text>
                  <Flex gap="lg" wrap="wrap">
                    <ImageUpload
                      control={control}
                      name="config.logoImage"
                      onPreview={onPick("logoImage", PREVIEW_HOME)}
                      label="Logo image"
                      defaultImage={logoImage}
                      resize
                      removable
                      style={{ flex: "1 1 300px" }}
                    />
                    <ImageUpload
                      control={control}
                      name="config.headerImage"
                      onPreview={onPick("headerImage", PREVIEW_HOME)}
                      label="Header image"
                      defaultImage={headerImage}
                      resize
                      aspectRatio={16 / 9}
                      removable
                      style={{ flex: "1 1 300px" }}
                    />
                  </Flex>
                </section>

                <section>
                  <Title order={3}>Default cover images</Title>
                  <Text c="dimmed" size="sm" mb="md">
                    Custom images used for profiles, sessions, sponsors, and
                    speakers.
                  </Text>
                  <Flex gap="lg" wrap="wrap">
                    <ImageUpload
                      control={control}
                      name="config.defaultProfileCover"
                      onPreview={onPick("defaultProfileCover", PREVIEW_COVERS)}
                      label="Default profile cover"
                      defaultImage={defaultProfileCover}
                      resize
                      aspectRatio={16 / 9}
                      removable
                      style={{ flex: "1 1 300px" }}
                    />
                    <ImageUpload
                      control={control}
                      name="config.defaultSessionCover"
                      onPreview={onPick("defaultSessionCover", PREVIEW_COVERS)}
                      label="Default session cover"
                      defaultImage={defaultSessionCover}
                      resize
                      aspectRatio={16 / 9}
                      removable
                      style={{ flex: "1 1 300px" }}
                    />
                    <ImageUpload
                      control={control}
                      name="config.defaultSponsorCover"
                      onPreview={onPick("defaultSponsorCover", PREVIEW_COVERS)}
                      label="Default sponsor cover"
                      defaultImage={defaultSponsorCover}
                      resize
                      aspectRatio={16 / 9}
                      removable
                      style={{ flex: "1 1 300px" }}
                    />
                    <ImageUpload
                      control={control}
                      name="config.defaultSpeakerCover"
                      onPreview={onPick("defaultSpeakerCover", PREVIEW_COVERS)}
                      label="Default speaker cover"
                      defaultImage={defaultSpeakerCover}
                      resize
                      aspectRatio={16 / 9}
                      removable
                      style={{ flex: "1 1 300px" }}
                    />
                  </Flex>
                </section>

                <section>
                  <Title order={3}>Accent color</Title>
                  <Text c="dimmed" size="sm" mb="md">
                    Accent color that applies to styling of the mobile event
                    application.
                  </Text>
                  <ColorInput
                    name="config.accentColor"
                    control={control}
                    withEyeDropper={false}
                    withPicker
                    swatches={colorSwatches}
                    swatchesPerRow={6}
                    disallowInput={false}
                    closeOnColorSwatchClick
                  />
                </section>

                <Button
                  type="submit"
                  disabled={!isDirty}
                  loading={isLoading}
                  style={{ alignSelf: "flex-start" }}
                >
                  Save
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>

        {/* Below `lg` there is no room for the frame beside the settings it
            explains, so it moves under them rather than disappearing. */}
        {showPreview && (
          <MobilePreview
            eventId={data.id}
            config={previewConfig}
            path={previewPath}
            cover={previewCover}
            views={PREVIEW_VIEWS}
            controls={
              previewPath === PREVIEW_COVERS && (
                <SegmentedControl
                  fullWidth
                  size="xs"
                  value={previewCover}
                  onChange={setPreviewCover}
                  data={COVERS.map((cover) => ({
                    value: cover.field,
                    label: cover.label,
                  }))}
                />
              )
            }
            onPathChange={setPreviewPath}
            isDirty={isDirty}
          />
        )}
      </Flex>
    </Container>
  );
};

export default CustomizationTab;
